import React, { useState } from 'react';
import Dialog from '@dashboard/components/Dialog';
import { Form } from '@dashboard/components/Formik';
import Tooltip from '@dashboard/components/Tooltip';
import clsx from 'clsx';
import { ReceiptRefundIcon } from '@heroicons/react/24/outline';
import Link from '@/dashboard/components/Link';
import { formatPriceForInput } from '@/dashboard/services/formatting/price';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { fetcher } from '@dashboard/services/request';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { toast } from '@dashboard/context/ToastContext';
import { formatPrice, formatPriceInput } from '../../../services/formatting/price';
import { calculateMaximumRefundAmount } from '../../../services/payments';
import { chargeKey } from '@dashboard/lib/queryKeys';

const PaymentsChargeRefundDialog = ({ payment }) => {
  const [open, setOpen] = useState(false);
  const { token } = useAuth();
  const queryClient = useQueryClient();

  const maximumRefundAmount = calculateMaximumRefundAmount(payment);

  const refundCharge = useMutation({
    mutationFn: values =>
      fetcher(`/sellers/${payment.seller}/charges/${payment._id}/refund`, token, {
        method: 'POST',
        body: JSON.stringify(values),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: chargeKey(payment._id) });

      toast({
        title: 'Payment refunded',
        type: 'success',
      });

      setOpen(false);
    },
  });

  const tooltipLabel =
    payment.status === 'refunded'
      ? 'This payment has been refunded'
      : maximumRefundAmount === 0
        ? 'Nothing to refund yet'
        : '';

  if (!payment.stripePaymentIntentId) {
    return null;
  }

  return (
    <>
      <Tooltip triggerClassName="w-fit" label={tooltipLabel} side="right">
        <Link
          onClick={() => setOpen(true)}
          className={clsx('w-full flex gap-x-2 items-center no-underline', {
            'pointer-events-none opacity-50 cursor-not-allowed':
              payment.status === 'refunded' || maximumRefundAmount === 0,
          })}
        >
          <>
            <ReceiptRefundIcon className="w-4 h-4" />
            Refund payment
          </>
        </Link>
      </Tooltip>

      <Dialog
        title="Refund payment"
        cancelButton={null}
        confirmButton={null}
        size="md"
        open={open}
        onCancel={() => setOpen(false)}
        description={'Issue a partial or full refund.'}
      >
        {() => (
          <Form
            showState={false}
            initialValues={{
              reason: '',
              refundReason: '',
              refundAmount: formatPriceForInput({
                amount: maximumRefundAmount,
                currency: payment.currency,
                style: 'decimal',
              }),
              sendRefundEmail: true,
            }}
            onSubmit={async values => {
              try {
                const data = await refundCharge.mutateAsync({
                  ...values,
                  refundAmount: formatPriceInput(values.refundAmount, payment.currency),
                });

                return {
                  data,
                };
              } catch (error) {
                return {
                  error,
                };
              }
            }}
            onSuccess={() => {
              queryClient.invalidateQueries({ queryKey: chargeKey(payment._id) });

              toast({
                title: 'Payment refunded',
                type: 'success',
              });

              setOpen(false);
            }}
            fields={() => [
              {
                name: 'refundAmount',
                label: `Refund amount`,
                tags: [
                  `${formatPrice({ amount: maximumRefundAmount, currency: payment.currency })} left to refund`,
                ],
                type: 'number',
                required: true,
              },
              {
                name: 'refundReason',
                label: 'Reason',
                type: 'select',
                required: true,
                placeholder: 'Select a reason',
                options: [
                  { value: 'duplicate', label: 'Duplicate' },
                  { value: 'fraudulent', label: 'Fraudulent' },
                  { value: 'requested_by_customer', label: 'Requested by customer' },
                ],
              },
              {
                name: 'reason',
                label: 'Note',
                tags: ['not shown to customer'],
                type: 'textarea',
              },
              {
                name: 'sendRefundEmail',
                label: 'Send refund email to customer',
                type: 'checkbox',
              },
            ]}
            formSubmitAlign="end"
            submitButton={{
              label: 'Refund payment',
              appearance: 'primary',
            }}
          />
        )}
      </Dialog>
    </>
  );
};

export default PaymentsChargeRefundDialog;
